import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdClose } from 'react-icons/md';
import PasswordLoginForm from './PasswordLoginForm';
import RegisterQuickForm from './RegisterQuickForm';
import { loginWithPassword, registerWithPassword } from '../../services/api';

export default function AuthModal({ isOpen, onClose, initialMode = 'login', redirectTo }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState(initialMode);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setMode(initialMode);
    setLoginError('');
  }, [isOpen, initialMode]);

  if (!isOpen) return null;

  const getDestination = (user) => {
    if (user?.profileComplete === false) return '/complete-profile';
    if (redirectTo && redirectTo.startsWith('/')) return redirectTo;
    return '/dashboard';
  };

  const handleLogin = async (email, password, rememberMe) => {
    setLoginError('');
    setIsSubmitting(true);
    try {
      const result = await loginWithPassword(email, password, { rememberMe });
      if (!result.success) {
        setLoginError(result.error || 'Prijava nije uspela.');
        return;
      }
      onClose?.();
      navigate(getDestination(result.user), { replace: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegister = async (payload) => {
    setIsSubmitting(true);
    try {
      const result = await registerWithPassword(payload);
      if (!result.success) {
        setLoginError(result.error || 'Registracija nije uspela.');
        return;
      }
      onClose?.();
      navigate(getDestination(result.user), { replace: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[12000] bg-slate-950/55 backdrop-blur-sm flex items-stretch sm:items-center sm:justify-center p-0 sm:p-4" onClick={onClose}>
      <div
        className="w-full h-full sm:h-auto sm:max-h-[92vh] sm:w-[min(640px,96vw)] bg-white dark:bg-gray-900 sm:rounded-[32px] shadow-2xl border border-white/40 dark:border-gray-700/60 animate-scale-in overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm px-5 sm:px-8 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              {mode === 'login' ? 'Uloguj se' : 'Napravi nalog'}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {mode === 'login' ? 'Dobrodošli nazad u Nannity.' : 'Kratko kreiranje naloga, pa onboarding.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-11 h-11 rounded-full border-2 border-primary-600 text-primary-700 dark:text-primary-300 dark:border-primary-400 flex items-center justify-center hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
            aria-label="Zatvori"
          >
            <MdClose className="text-2xl" />
          </button>
        </div>

        <div className="px-5 sm:px-8 py-6">
          <div className="inline-flex p-1 rounded-2xl bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 mb-5">
            <button
              type="button"
              onClick={() => { setMode('login'); setLoginError(''); }}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${mode === 'login' ? 'bg-white dark:bg-gray-700 text-primary-600' : 'text-gray-600 dark:text-gray-400'}`}
            >
              Uloguj se
            </button>
            <button
              type="button"
              onClick={() => { setMode('register'); setLoginError(''); }}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${mode === 'register' ? 'bg-white dark:bg-gray-700 text-primary-600' : 'text-gray-600 dark:text-gray-400'}`}
            >
              Registruj se
            </button>
          </div>

          {mode === 'login' ? (
            <PasswordLoginForm
              onSubmit={handleLogin}
              onForgotPassword={() => navigate('/login')}
              isLoading={isSubmitting}
              error={loginError}
              setError={setLoginError}
              hideRegisterLink
            />
          ) : (
            <RegisterQuickForm
              onSubmit={handleRegister}
              isSubmitting={isSubmitting}
              onSwitchToLogin={() => setMode('login')}
            />
          )}
        </div>
      </div>
    </div>
  );
}
